import React, {useState} from "react";
import SEO from "../../components/SEO";
import {
    BankOutlined,
    BarChartOutlined,
    FileTextOutlined,
    InboxOutlined,
    MailOutlined,
    SafetyOutlined,
    SettingOutlined,
} from "@ant-design/icons";

const pageSEOSettings = {
    title: "User Guide",
};

const SECTIONS = [
    {
        id: "overview",
        label: "Overview",
        icon: <FileTextOutlined/>,
        intro: "ValidationStation is an invoice OCR and validation platform. Documents are received, processed, reviewed, corrected and submitted to Shipsure. Use the sidebar to move between Invoices, Email Queues, Reports and (for admins) the Admin tools.",
        items: [
            {title: "Workspace navigation", body: "The left sidebar groups the app into a Workspace area (Invoices, Email Queues, Reports) and, for admins, an Admin area. The bar at the top of each page shows the title and the main actions for that screen."},
            {title: "Your access", body: "Open the profile panel (your avatar) to see your email, role (Admin / Manager) and the Offices, Fleets, Entities and Vessel Responsibilities you have access to. Your access determines which invoices and customers you can see and act on."},
            {title: "Roles", body: "Standard users review and submit invoices. Managers additionally see Reports. Admins see Reports and the full Admin area (permitted emails, mapped KVPs, document labels and suppliers)."},
        ],
    },
    {
        id: "invoices-list",
        label: "Invoice List",
        icon: <FileTextOutlined/>,
        intro: "The Invoices screen lists documents by processing status. Use it to find an invoice and open it for review.",
        items: [
            {title: "Filtering & paging", body: "Switch between status groups (e.g. latest, to-be-processed, or a specific status) and page through results. The list keeps your place so you can move between invoices and return to the same page."},
            {title: "Opening an invoice", body: "Select a row to open the Invoice Details page, where you review the extracted data against the source document."},
            {title: "Bulk updates", body: "Tick the checkboxes against one or more rows, then use the Bulk menu to action them together. You can Reject, Delete, Mark as On Hold or Mark as Complete (Mark as Complete is available to Admins and Managers). Each option shows how many of the selected rows are eligible, and invoices already in that state — or already completed — are skipped automatically."},
        ],
    },
    {
        id: "invoices-detail",
        label: "Invoice Details",
        icon: <FileTextOutlined/>,
        intro: "The Invoice Details page shows the source document alongside the extracted fields. Review, correct and submit from here.",
        items: [
            {title: "Invoice Header panel", body: "Holds the core header fields — Invoice No, PO Reference, Payment Term, Invoice Date, Due Date, Department, Proforma flag and a Description. Corrected fields are highlighted so changes are easy to spot. Confidence indicators flag values the OCR was unsure about."},
            {title: "Supplier search", body: "Search for the supplier by name. Each result card shows the supplier name, address, type, purchase count, currency, country and badges for Recovery / Invoice / Vetted / bank-details. Selecting a supplier links the invoice to that supplier record."},
            {title: "Line items & totals", body: "Review the description lines, quantities, unit prices and amounts. Sub-total, tax and invoice total are validated against the line items, and mismatches are flagged for correction."},
            {title: "Finance tax fields", body: "For applicable suppliers a Net Value and Finance Tax Code are shown. If you enter one, the other becomes mandatory — submission is blocked until both are provided."},
            {title: "Banking details", body: "Supplier bank details (bank name, currency, account number, SWIFT, IBAN, sort code, IFSC and address) are shown for verification."},
            {title: "Supporting documents", body: "The Supporting Documents panel lists the files attached to the invoice, with a count of how many are present. Upload additional files (you can add several at once) to support the invoice, and remove ones that are no longer needed. Added and removed documents are applied when you Save or Submit, and the attachment list refreshes once the changes are stored."},
            {title: "Header navigation", body: "Use Prev / Next in the top bar to move through the invoices in your current list without returning to the listing. Close returns you to the Invoice List."},
            {title: "Actions", body: "Save stores your corrections. Submit validates the invoice (customer / vessel-management checks, invoice-date checks, mandatory finance fields and currency selection) and creates it in Shipsure. You can also Reject, Delete or Reprocess an invoice where permitted."},
        ],
    },
    {
        id: "email-queues",
        label: "Email Queues",
        icon: <InboxOutlined/>,
        intro: "Email Queues show inbound emails that feed the invoice pipeline, organised into folders.",
        items: [
            {title: "Categories", body: "Emails are grouped into For Review, Inbox, Processed and Reviewed Ignore. Use these to triage what still needs attention versus what has been handled."},
            {title: "Email details", body: "Open an email to see its content and attachments and to action it from the queue."},
            {title: "Merge attachments", body: "When an email arrives with several attachments that belong to a single invoice, use Merge attachments to combine them into one document. In the merge dialog, tick the attachments you want to include — the order you select them sets the merge order — and reorder the selected list with the up/down controls before confirming. You must select at least one attachment, and a confirmation is shown once the merge completes."},
        ],
    },
    {
        id: "reports",
        label: "Reports",
        icon: <BarChartOutlined/>,
        intro: "Reports (Managers, Vessel Managers and Admins) summarise processing and email volumes and let you export the data.",
        items: [
            {title: "Document stats", body: "View invoice volume by processing status — Submitted, Processed, Rejected, Failed, On Hold, Duplicates and Under Review. Selecting the Failed status includes both failed-validation and awaiting-processing documents."},
            {title: "Filters", body: "Narrow the data by status, source, mailbox and a date range. The charts and exports respect the active filters."},
            {title: "Email stats", body: "See email volumes by folder (For Review, Inbox, Processed, Reviewed Ignore) over the chosen period."},
            {title: "Downloads", body: "Generate and download reports as PDF or Excel for the current filters."},
        ],
    },
    {
        id: "admin",
        label: "Admin",
        icon: <SafetyOutlined/>,
        adminOnly: true,
        intro: "Admin tools (Admins only) configure how documents are matched and which suppliers and emails are recognised.",
        items: [
            {title: "Permitted Emails", body: "Maintain the list of email addresses allowed to submit documents into the system."},
            {title: "Mapped KVPs", body: "Manage key-value mappings used to normalise extracted field values."},
            {title: "Document Labels", body: "Configure document-label mappings used during classification."},
            {title: "Suppliers", body: "Browse and open supplier records, including their type, addresses, currencies and bank details. Enter a supplier name, pick a country, then select Search to filter the list.", icon: <BankOutlined/>},
            {title: "Mailbox Users", body: "Control which users have access to each queue mailbox. Add a user by entering their email address and selecting a mailbox, edit an existing entry to move it to another mailbox or mark it inactive, and remove access you no longer need. Filter the list by user email or mailbox, then select Search.", icon: <MailOutlined/>},
            {title: "OCR Configuration", body: "Manage the OCR settings used when reading a supplier's documents. Each entry names the supplier and a configuration type, and sets the model and locale used to read the document, the pages to read, and any extra query fields to pull out on top of the model defaults. Search by supplier name, and add, edit or delete entries as needed.", icon: <SettingOutlined/>},
        ],
    },
];

const UserGuide = ({admin}) => {
    const sections = SECTIONS.filter((s) => !s.adminOnly || admin);
    const [activeId, setActiveId] = useState(sections[0]?.id);

    const goTo = (id) => {
        setActiveId(id);
        const el = document.getElementById(`guide-${id}`);
        if (el) el.scrollIntoView({behavior: "smooth", block: "start"});
    };

    return (
        <div>
            <SEO {...pageSEOSettings}/>

            {/* Page header */}
            <div className="px-4 sm:px-6 py-5 bg-white border-b-2 border-b-[#CDD6DB]">
                <div className="text-[#020A08] text-[28px] font-semibold leading-tight">
                    User Guide
                </div>
                <div className="text-sm text-gray-500 mt-1">
                    How to use ValidationStation — from receiving an invoice to submitting it to Shipsure.
                </div>
            </div>

            <div className="px-4 sm:px-6 py-6 flex flex-col lg:flex-row gap-6 items-start">
                {/* Table of contents */}
                <nav className="w-full lg:w-[240px] flex-shrink-0 lg:sticky lg:top-4">
                    <div className="bg-white rounded-lg border border-[#E4E7EE] p-2">
                        {sections.map((s) => (
                            <button
                                key={s.id}
                                onClick={() => goTo(s.id)}
                                className={`w-full flex items-center gap-2 text-left px-3 py-2 rounded-md text-[13px] transition-colors ${
                                    activeId === s.id
                                        ? "bg-[#ECF9EE] text-[var(--primary-color)] font-semibold"
                                        : "text-[#374152] hover:bg-[#F7F8FB]"
                                }`}
                            >
                                <span className="text-[14px]">{s.icon}</span>
                                <span className="flex-1">{s.label}</span>
                            </button>
                        ))}
                    </div>
                </nav>

                {/* Content */}
                <div className="flex-1 min-w-0 space-y-5">
                    {sections.map((s) => (
                        <section
                            key={s.id}
                            id={`guide-${s.id}`}
                            className="bg-white rounded-lg border border-[#E4E7EE] scroll-mt-4"
                        >
                            <div className="px-5 py-4 border-b border-[#F1F3F7] flex items-center gap-2.5">
                                <span className="text-[var(--primary-color)] text-[18px]">{s.icon}</span>
                                <div className="text-[16px] font-semibold text-[#020A08]">{s.label}</div>
                            </div>
                            <div className="px-5 py-4">
                                <p className="text-[13.5px] text-[#55607A] leading-relaxed mb-4">{s.intro}</p>
                                <div className="space-y-3">
                                    {s.items.map((item, i) => (
                                        <div key={i} className="border border-[#F1F3F7] rounded-md p-3">
                                            <div className="text-[13px] font-semibold text-[#0B1220] mb-1 flex items-center gap-2">
                                                {item.icon && <span className="text-[#7A849A]">{item.icon}</span>}
                                                {item.title}
                                            </div>
                                            <div className="text-[13px] text-[#55607A] leading-relaxed">{item.body}</div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </section>
                    ))}
                </div>
            </div>
        </div>
    );
};

export default UserGuide;
